import { useCallback, useMemo } from "react";
import { logger } from "@platform/browser-logger";

type StorageType = "local" | "session";

type UseStorageReturn = {
  getStorageItem: <T>(key: string) => T | null;
  setStorageItem: <T>(key: string, value: T) => void;
  removeStorageItem: (key: string) => void;
};

/**
 * React hook for accessing browser storage (local/session).
 * Safe from JSON errors and provides memoized functions.
 */
export function useStorage(type: StorageType = "session"): UseStorageReturn {
  const storage = type === "local" ? localStorage : sessionStorage;

  const setStorageItem = useCallback(
    function <T>(key: string, value: T): void {
      try {
        storage.setItem(key, JSON.stringify(value));
      } catch (err) {
        logger.error(`Error setting ${key} in ${type}Storage`, err as Error);
      }
    },
    [storage, type],
  );

  const getStorageItem = useCallback(
    function <T>(key: string): T | null {
      try {
        const value = storage.getItem(key);
        return value ? (JSON.parse(value) as T) : null;
      } catch (err) {
        logger.error(`Error getting ${key} from ${type}Storage`, err as Error);
        return null;
      }
    },
    [storage, type],
  );

  const removeStorageItem = useCallback(
    function (key: string): void {
      try {
        storage.removeItem(key);
      } catch (err) {
        logger.error(`Error removing ${key} from ${type}Storage`, err as Error);
      }
    },
    [storage, type],
  );

  return useMemo(
    () => ({ getStorageItem, setStorageItem, removeStorageItem }),
    [getStorageItem, setStorageItem, removeStorageItem],
  );
}
